import { buildRowLookup } from '../../analysisHelpers/matrixRowLookup'
import resolveRecordRow from '../../analysisHelpers/resolveRecordRow'

/**
 * @typedef {object} TransitionNode
 * @property {string} name - Label, e.g. "Start 3: 40%"
 * @property {'start'|'stop'} side - Which column the node sits in
 * @property {number} bucket - The 1–5 scale point
 * @property {number} percent - % of pairs in this bucket on this side
 */

/**
 * @typedef {object} TransitionResult
 * @property {'empty'|'data'} mode
 * @property {Array<TransitionNode>} nodes
 * @property {Array<{source:number, target:number, value:number, stayed:boolean, startBucket:number, anchor?:boolean}>} links - `anchor:true` links are invisible placeholders that keep empty levels in their column
 * @property {number} totalPairs - Number of paired answers (one answer on one question for one person)
 */

/**
 * Builds a percentage-distribution transition Sankey for an MSI effect: pairs each
 * (Persona, RowTitle) answer across the first and last measure moment, pools every
 * row into one 1–5 distribution, and returns recharts `{ nodes, links }` with the
 * share of answers per start/stop bucket. Negatively-worded rows are flipped
 * (`lo + hi − raw`) before bucketing so an improvement always moves toward the top.
 *
 * A "unit" is one answer on one question; a person answering 3 questions contributes
 * 3 units. Only people answered at BOTH the first and last moment count.
 * AI-CONTEXT: See docs/features/msi-transition-sankey.md
 *
 * @param {object} params
 * @param {Array<{Persona?: string, RowID?: string, RowTitle?: string, _rowTitle?: string, Input?: number|string, MomentPosition?: number|string}>} params.records
 * @param {Array<{ID?: string, Title?: string, posNeg?: string}>} params.rows - rawRowDefs (real MatrixQuestionRows)
 * @param {number|string} params.lowerScale
 * @param {number|string} params.upperScale
 * @returns {TransitionResult}
 */
const buildTransitionSankeyData = ({ records, rows, lowerScale, upperScale }) => {
  const lo = Number(lowerScale)
  const hi = Number(upperScale)
  const empty = { mode: 'empty', nodes: [], links: [], totalPairs: 0 }

  if (!Array.isArray(records) || records.length === 0) return empty
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return empty

  // Rows by RowID first: an answer given under a row's old wording still pairs
  // with one given under the new, and keeps its valence.
  // AI-CONTEXT: See docs/features/matrix-rowid-join.md
  const lookup = buildRowLookup(rows || [])

  const positions = records
    .map((r) => Number(r.MomentPosition))
    .filter((n) => Number.isFinite(n))
  if (positions.length === 0) return empty

  const firstPos = Math.min(...positions)
  const lastPos = Math.max(...positions)
  if (firstPos === lastPos) return empty // need at least two moments to pair

  // Pair each (person, row) across first and last moment.
  const byKey = {}
  records.forEach((r) => {
    const persona = r.Persona
    if (!persona) return
    const num = Number(r.Input)
    if (!Number.isFinite(num)) return
    const pos = Number(r.MomentPosition)
    const row = resolveRecordRow(lookup, r)
    const rowKey = row ? row.key : `title:${r.RowTitle}`
    const key = `${persona}__${rowKey}`
    const entry = (byKey[key] ||= { negative: !!row && row.posNeg === 'negative', first: null, last: null })
    if (pos === firstPos) entry.first = num
    if (pos === lastPos) entry.last = num
  })

  const toBucket = (negative, raw) => {
    const adjusted = negative ? (lo + hi - raw) : raw
    return Math.min(hi, Math.max(lo, Math.round(adjusted)))
  }

  const startCount = {}
  const endCount = {}
  const linkCount = {}
  let totalPairs = 0

  Object.values(byKey).forEach(({ negative, first, last }) => {
    if (first === null || last === null) return
    const s = toBucket(negative, first)
    const e = toBucket(negative, last)
    startCount[s] = (startCount[s] || 0) + 1
    endCount[e] = (endCount[e] || 0) + 1
    const lk = `${s}->${e}`
    linkCount[lk] = (linkCount[lk] || 0) + 1
    totalPairs += 1
  })

  if (totalPairs === 0) return empty

  const pct = (n) => Math.round(((n || 0) / totalPairs) * 100)

  // Always render the full lo..hi scale on both sides, high level on top, even
  // for levels with no respondents — so a goal distribution shows in full and the
  // chart always matches the scale (and the goal-setting modal).
  const buckets = []
  for (let b = hi; b >= lo; b -= 1) buckets.push(b)

  const nodes = []
  const startIndex = {}
  const endIndex = {}

  buckets.forEach((bucket) => {
    startIndex[bucket] = nodes.length
    nodes.push({ name: `Start ${bucket}: ${pct(startCount[bucket])}%`, side: 'start', bucket, percent: pct(startCount[bucket]) })
  })
  buckets.forEach((bucket) => {
    endIndex[bucket] = nodes.length
    nodes.push({ name: `Stop ${bucket}: ${pct(endCount[bucket])}%`, side: 'stop', bucket, percent: pct(endCount[bucket]) })
  })

  // Real transition bands.
  const links = Object.entries(linkCount).map(([lk, count]) => {
    const [s, e] = lk.split('->').map(Number)
    return { source: startIndex[s], target: endIndex[e], value: count, stayed: s === e, startBucket: s }
  })

  // One invisible anchor link per level keeps every node connected, so recharts
  // places it in the correct column even when it has 0% (empty levels would
  // otherwise collapse to the left column). Anchor links render nothing — the
  // component skips them. Pushed AFTER the real links so links[0] stays a band.
  buckets.forEach((bucket) => {
    links.push({ source: startIndex[bucket], target: endIndex[bucket], value: 1e-6, anchor: true, stayed: true, startBucket: bucket })
  })

  return { mode: 'data', nodes, links, totalPairs }
}

export default buildTransitionSankeyData
